import {
  CarePlanActivityCode,
  CarePlanActivityCodeDisplay,
  createCodeableConcept,
  createFhirDate,
  createReference,
  createReferenceFromResource,
  getCodes,
  getFirstCodeFromCodableConcepts,
  isValidReference,
  LanguagePreference,
  ServiceRequestCode,
  system,
  type WithId,
} from '@quromedical/fhir-common';
import { compact, first, groupby, orderby, sum } from '@quromedical/utils';
import { format } from 'date-fns';
import type {
  CarePlan,
  CarePlanActivity,
  Coverage,
  Patient,
  Questionnaire,
  RiskAssessment,
} from 'fhir/r4';
import { mapEncounterToAdmission } from '../admission';
import type { PatientCall } from '../call';
import { mapFhirQuestionnaireToModel } from '../questionnaire';

import {
  BaseCarePlanSchema,
  type CarePlanByPatient,
  CarePlanByPatientSchema,
  CarePlanSchema,
  type CreateCarePlanEscalation,
  type CarePlan as ModelCarePlan,
} from './schema';

export const mapRiskAssessmentToPriorityScore = (
  riskAssessment: WithId<RiskAssessment>,
): number => {
  return sum(
    riskAssessment.prediction?.map(
      (prediction) => prediction.probabilityDecimal,
    ),
  );
};

export const mapModelToFhirCarePlan = (
  carePlan: ModelCarePlan,
): WithId<CarePlan> => {
  const {
    id,
    activity,
    created,
    patient,
    period,
    status,
    title,
    admission,
    author,
    careTeam,
    description,
    intent,
    instantiatesCanonical,
  } = carePlan;

  const activityResult = activity.map((activity) => {
    const {
      code,
      description,
      instantiatesCanonical,
      kind,
      scheduledPeriod,
      status,
    } = activity.detail;
    return {
      detail: {
        code: createCodeableConcept(code),
        description,
        kind,
        status,
        instantiatesCanonical,
        scheduledPeriod,
      },
      reference: activity.reference,
    };
  });

  return {
    id,
    resourceType: 'CarePlan',
    status,
    period,
    subject: patient.reference,
    encounter: admission,
    intent,
    title,
    created,
    activity: activityResult,
    author,
    careTeam,
    description,
    instantiatesCanonical,
  };
};

interface CarePlanProps {
  carePlan: WithId<CarePlan>;
  patient: WithId<Patient>;
  riskAssessment: WithId<RiskAssessment>;
  coverage?: WithId<Coverage>;
  questionnaires?: WithId<Questionnaire>[];
  call?: PatientCall;
}

export const mapFhirCarePlanToModel = (input: CarePlanProps): ModelCarePlan => {
  const {
    patient,
    riskAssessment,
    coverage,
    questionnaires = [],
    call,
  } = input;
  const {
    id,
    status,
    period,
    subject,
    encounter,
    intent,
    title,
    created,
    activity,
    author,
    careTeam,
    description,
    instantiatesCanonical,
  } = input.carePlan;

  const subjectReference = isValidReference(subject) ? subject : undefined;
  const encounterReference = isValidReference(encounter)
    ? encounter
    : undefined;
  const authorReference = isValidReference(author) ? author : undefined;
  const careTeamReference = careTeam?.map((t) =>
    isValidReference(t) ? t : undefined,
  );

  const activityResult = activity?.map((activity) => {
    return {
      detail: {
        ...activity.detail,
        scheduledPeriod: activity.detail?.scheduledPeriod,
        code: first(getCodes(activity.detail?.code)),
      },
      reference: activity.reference,
    };
  });

  const priorityScore = mapRiskAssessmentToPriorityScore(riskAssessment);

  const languageCodableConcept = first(patient.communication)?.language;

  const languagePreference = languageCodableConcept
    ? (getFirstCodeFromCodableConcepts([
        languageCodableConcept,
      ]) as LanguagePreference)
    : LanguagePreference.English;

  const carePlanModel = {
    id,
    dailyQuestionnaires: questionnaires.map(mapFhirQuestionnaireToModel),
    title,
    status,
    period,
    patient: {
      reference: subjectReference,
      languagePreference,
    },
    admission: encounterReference,
    description,
    intent,
    author: authorReference,
    careTeam: compact(careTeamReference),
    created,
    activity: activityResult,
    instantiatesCanonical,
    priorityScore,
    call,
    coverage: coverage
      ? {
          coverage: coverage?.id,
          membershipNumber: coverage.subscriberId,
        }
      : undefined,
  };

  const parsed = CarePlanSchema.safeParse(carePlanModel);

  if (!parsed.success) {
    throw parsed.error;
  }

  return parsed.data;
};

const normalizeDate = (date: string | undefined): string => {
  if (!date) {
    return '';
  }
  return format(new Date(date), 'yyyy-MM-dd');
};

const mapCarePlanActivitiesToDay = (
  carePlanActivities: CarePlanActivity[],
  flags: WithId<fhir4.Flag>[],
): CarePlanByPatient['days'] | undefined => {
  const groupedActivities = groupby(carePlanActivities, (activity) => {
    const normalizedDate = normalizeDate(
      activity.detail?.scheduledPeriod?.start,
    );
    return normalizedDate;
  });

  // Filter the dictionary to only include keys (dates) up to the current date
  //TODO: Implement the logic to check if any of these activities are present in the fhir store
  const filteredGroupedActivities: CarePlanByPatient['days'] = Object.entries(
    groupedActivities,
  )
    .filter(([dateKey]) => new Date(dateKey) <= new Date()) // Filter for dates up to today
    .sort(
      ([dateKeyA], [dateKeyB]) =>
        new Date(dateKeyA).getTime() - new Date(dateKeyB).getTime(),
    )
    .map(([dateKey, activity], index) => {
      const hasAppointment = activity.some(
        (act) => act.detail?.kind === 'Appointment',
      );
      const hasPathology = activity.some(
        (act) =>
          act.detail?.kind === 'ServiceRequest' &&
          act.detail?.code?.coding?.some(
            (coding) => coding.code === ServiceRequestCode.PathologyRequest,
          ),
      );
      const hasEscalations = activity.some(
        (act) =>
          act.detail?.kind === 'ServiceRequest' &&
          act.detail?.code?.coding?.some(
            (coding) => coding.code === CarePlanActivityCode.Escalation,
          ),
      );

      const hasFlag = flags.some((flag) => flag.period?.start === dateKey);

      return {
        dayNumber: index + 1,
        date: dateKey,
        escalations: hasEscalations,
        flags: hasFlag,
        appointments: hasAppointment,
        pathology: hasPathology,
        radiology: false,
      };
    });

  return orderby(filteredGroupedActivities, (act) => act.dayNumber, 'desc');
};

export const mapFhirToCarePlanByPatientModel = (
  carePlan: WithId<fhir4.CarePlan>,
  patient: WithId<fhir4.Patient>,
  encounter: WithId<fhir4.Encounter>,
  flags: WithId<fhir4.Flag>[],
): CarePlanByPatient => {
  const admission = mapEncounterToAdmission(encounter);
  const pauses = admission.statusHistory?.filter(
    (status) => status.status === 'onleave',
  );

  const days = mapCarePlanActivitiesToDay(carePlan.activity || [], flags || []);

  const result = {
    patient: createReferenceFromResource(patient),
    status: carePlan.status,
    period: carePlan.period,
    rfa: first(admission.reasonOfAdmission),
    days,
    pauses: pauses?.map((pause) => {
      return {
        start: pause.period?.start,
        end: pause.period?.end,
      };
    }),
  };

  const parsed = CarePlanByPatientSchema.safeParse(result);

  if (!parsed.success) {
    throw parsed.error;
  }

  return parsed.data;
};

export const mapModelToCreateServiceRequestEscalation = (
  escalation: CreateCarePlanEscalation,
): fhir4.ServiceRequest => {
  const {
    patient,
    admission,
    carePlan,
    careTeam,
    note,
    priority = 'stat',
    requestor,
  } = escalation;

  return {
    resourceType: 'ServiceRequest',
    subject: createReference('Patient', patient),
    requester: requestor
      ? createReference('Practitioner', requestor)
      : undefined,
    encounter: createReference('Encounter', admission),
    occurrenceDateTime: createFhirDate(new Date()),
    note: note ? [{ text: note }] : undefined,
    intent: 'order',
    code: createCodeableConcept(
      CarePlanActivityCode.Escalation,
      CarePlanActivityCodeDisplay[CarePlanActivityCode.Escalation],
      CarePlanActivityCodeDisplay[CarePlanActivityCode.Escalation],
      system,
    ),
    performer: careTeam.map((member) => createReference('CareTeam', member)),
    priority,
    status: 'active',
    basedOn: [createReference('CarePlan', carePlan)],
  };
};

export const mapFhirToServiceRequest = (
  escalation: WithId<fhir4.ServiceRequest>,
) => {
  const {
    id,
    subject,
    requester,
    encounter,
    occurrenceDateTime,
    note,
    intent,
    performer,
    priority,
    status,
    basedOn,
    code,
  } = escalation;

  const subjectReference = isValidReference(subject) ? subject : undefined;
  const encounterReference = isValidReference(encounter)
    ? encounter
    : undefined;
  const requesterReference = isValidReference(requester)
    ? requester
    : undefined;
  const careTeamReference = performer?.map((t) =>
    isValidReference(t) ? t : undefined,
  );

  return {
    id,
    status,
    intent,
    basedOn,
    note: note?.map((n) => n.text),
    occurrenceDateTime,
    category: first(getCodes(code)) ?? CarePlanActivityCode.Escalation,
    subject: subjectReference,
    requester: requesterReference,
    performer: first(careTeamReference),
    priority: priority,
    encounter: encounterReference,
  };
};

export const mapFhirCarePlanToModels = (input: WithId<CarePlan>) => {
  const {
    id,
    status,
    period,
    subject,
    encounter,
    intent,
    title,
    created,
    activity,
    author,
    careTeam,
    description,
    instantiatesCanonical,
  } = input;

  const subjectReference = isValidReference(subject) ? subject : undefined;
  const encounterReference = isValidReference(encounter)
    ? encounter
    : undefined;
  const authorReference = isValidReference(author) ? author : undefined;
  const careTeamReference = careTeam?.map((t) =>
    isValidReference(t) ? t : undefined,
  );

  const activityResult = activity?.map((activity) => {
    return {
      detail: {
        ...activity.detail,
        scheduledPeriod: activity.detail?.scheduledPeriod,
        code: first(getCodes(activity.detail?.code)),
      },
      reference: activity.reference,
    };
  });

  const carePlanModel = {
    id,
    title,
    status,
    period,
    patient: subjectReference,
    admission: encounterReference,
    description,
    intent,
    author: authorReference,
    careTeam: compact(careTeamReference),
    created,
    activity: activityResult,
    instantiatesCanonical,
  };
  const { success, data, error } = BaseCarePlanSchema.safeParse(carePlanModel);

  if (!success) {
    throw error;
  }

  return data;
};
